import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

const DEFAULT_PAGE_SIZE = 50;

const DEFAULT_ENTITY_LABELS = {
  order: "Order",
  equipment: "Equipment",
  shipment: "Shipment",
  consignee_shipper: "Consignee / Shipper",
  master_load: "Master Load",
  stops: "Stops",
  rates: "Rates",
  documents: "Documents",
  notes: "Notes",
};

function flattenChanges(historyData = []) {
  const flattened = [];
  
  historyData.forEach((group) => {
    const user = group?.modifiedBy || "System";
    const dateTime = group?.modifiedAt || "";
    const changes = group?.changes || [];
    
    changes.forEach((change) => {
      flattened.push({
        user,
        dateTime,
        section: change?.entity || "Unknown",
        field: change?.fieldName || "",
        previous: normalizeDisplayValue(change?.previousValue),
        new: normalizeDisplayValue(change?.newValue),
      });
    });
  });
  
  return flattened;
}

function normalizeDisplayValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

export default function ReusableHistoryViewer({
  fetchHistoryPage,
  reloadKey,
  title = "History Search",
  searchPlaceholder = "Search by user, date, section, field or value",
  pageSize = DEFAULT_PAGE_SIZE,
  entityLabels = DEFAULT_ENTITY_LABELS,
}) {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchDate, setSearchDate] = useState("");

  const containerRef = useRef(null);
  const sentinelRef = useRef(null);
  const pageRef = useRef(1);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);

  const resetHistory = useCallback(() => {
    setHistoryData([]);
    setHasMore(true);
    setLoading(false);
    pageRef.current = 1;
    loadingRef.current = false;
    hasMoreRef.current = true;
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) return;
    if (typeof fetchHistoryPage !== "function") return;

    loadingRef.current = true;
    setLoading(true);

    try {
      const response = await fetchHistoryPage(pageRef.current, pageSize);
      const newData = Array.isArray(response?.history) ? response.history : [];
      const pagination = response?.pagination || {};

      setHistoryData((prev) => {
        const merged = [...prev, ...newData];
        const uniqueMap = new Map();
        merged.forEach((item) => {
          uniqueMap.set(item.changeGroupId, item);
        });
        return Array.from(uniqueMap.values());
      });

      if (!pagination?.hasMore || newData.length === 0) {
        hasMoreRef.current = false;
        setHasMore(false);
      }

      pageRef.current += 1;
    } catch {
      hasMoreRef.current = false;
      setHasMore(false);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [fetchHistoryPage, pageSize]);

  useEffect(() => {
    resetHistory();
    loadMore();
  }, [loadMore, reloadKey, resetHistory]);

  const flattenedRows = useMemo(() => {
    return flattenChanges(historyData);
  }, [historyData]);

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    
    return flattenedRows.filter((row) => {
      const dateMatch = !searchDate || String(row.dateTime).startsWith(searchDate);
      if (!dateMatch) return false;
      
      if (!q) return true;
      
      return [
        row.user,
        row.dateTime,
        row.section,
        row.field,
        row.previous,
        row.new,
      ]
        .filter(Boolean)
        .some((val) => String(val).toLowerCase().includes(q));
    });
  }, [flattenedRows, searchDate, searchTerm]);

  useEffect(() => {
    const root = containerRef.current;
    const target = sentinelRef.current;
    if (!root || !target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first?.isIntersecting) loadMore();
      },
      {
        root,
        rootMargin: "180px 0px 220px 0px",
        threshold: 0.1,
      },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    <Box
      ref={containerRef}
      sx={{ height: "100%", overflowY: "auto", backgroundColor: "#fff" }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          px: 2,
          pt: 1.5,
          pb: 1,
          borderBottom: "1px solid #e4e8ee",
          background:
            "linear-gradient(180deg, rgba(247,250,255,1) 0%, rgba(255,255,255,0.98) 100%)",
        }}
      >
        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#44506b", mb: 1 }}>
          {title}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
            placeholder={searchPlaceholder}
            sx={{
              "& .MuiInputBase-input": {
                fontSize: "12px",
                py: 0.8,
              },
            }}
          />
          <TextField
            value={searchDate}
            onChange={(e) => setSearchDate(e.target.value)}
            size="small"
            type="date"
            label="Date"
            InputLabelProps={{ shrink: true }}
            sx={{
              minWidth: { xs: "100%", sm: "160px" },
              "& .MuiInputBase-input": {
                fontSize: "12px",
                py: 0.8,
              },
            }}
          />
        </Stack>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "none",
          borderRadius: 0,
          backgroundColor: "transparent",
          px: 2,
          "& .MuiTable-root": {
            backgroundColor: "#fff",
          },
        }}
      >
        <Table sx={{ minWidth: 850 }}>
          <TableHead
            sx={{
              backgroundColor: "#f8f9fb",
              "& .MuiTableCell-head": {
                fontWeight: 700,
                fontSize: "12px",
                color: "#44506b",
                borderBottom: "2px solid #e4e8ee",
                padding: "12px 16px",
                textAlign: "center",
              },
            }}
          >
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Modify Date Time</TableCell>
              <TableCell>Section</TableCell>
              <TableCell>Field</TableCell>
              <TableCell>Previous</TableCell>
              <TableCell>New</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 3 }}>
                  <Typography sx={{ fontSize: "12px", color: "#999" }}>
                    {loading ? "Loading..." : "No history records found"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map((row, idx) => (
                <TableRow
                  key={idx}
                  sx={{
                    "&:hover": {
                      backgroundColor: "#f8f9fb",
                    },
                    borderBottom: "1px solid #edf0f3",
                  }}
                >
                  <TableCell
                    sx={{
                      fontSize: "12px",
                      color: "#2f3542",
                      fontWeight: 600,
                      padding: "10px 16px",
                      textAlign: "center",
                    }}
                  >
                    {row.user}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "12px",
                      color: "#5b6473",
                      padding: "10px 16px",
                      textAlign: "center",
                    }}
                  >
                    {row.dateTime}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "12px",
                      color: "#3452a3",
                      fontWeight: 600,
                      padding: "10px 16px",
                      textAlign: "center",
                    }}
                  >
                    {row.section}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "12px",
                      color: "#2f3542",
                      padding: "10px 16px",
                      textAlign: "center",
                    }}
                  >
                    {row.field}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "12px",
                      color: "#6b7280",
                      backgroundColor: "#f8f9fb",
                      padding: "10px 16px",
                      maxWidth: "250px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      textAlign: "center",
                    }}
                    title={row.previous}
                  >
                    {row.previous}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "12px",
                      color: "#1f6f4a",
                      backgroundColor: "#eff9f2",
                      padding: "10px 16px",
                      maxWidth: "250px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      textAlign: "center",
                    }}
                    title={row.new}
                  >
                    {row.new}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <Box ref={sentinelRef} sx={{ height: "1px" }} />

      {!hasMore && filteredRows.length > 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: 2,
            borderTop: "1px solid #edf0f3",
            backgroundColor: "#fbfcfe",
          }}
        >
          <Typography sx={{ fontSize: "12px", color: "#999" }}>
            — End of history —
          </Typography>
        </Box>
      )}
    </Box>
  );
}
